import { useTranslation } from 'react-i18next';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Center, Spinner, SimpleGrid, Text, Flex, Field } from '@chakra-ui/react';
import { DrawerComponent } from '../../ui/drawerComponent';
import { Button as ButtonComponent, SectionCard, InputField } from '@themes/aegix';
import { useFloors, useCreateFloor, useReorderFloors } from '../../../hooks/queries/useFloors';
import { useUpdateBuilding } from '../../../hooks/queries/useBuildings';
import type { Building, Floor, Room } from '../../../services';
import { FloorDrawer } from './FloorDrawer';
import { toaster } from '../../ui/toaster';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MdDragIndicator } from 'react-icons/md';

interface FloorItem {
  id: string;
  numericId: number;
  name: string;
  ordering: number;
}

function SortableFloorItem({
  item,
  onClick,
}: {
  item: FloorItem;
  onClick: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Flex
      ref={setNodeRef}
      style={style}
      alignItems="center"
      px={3}
      py={2}
      bg="surface.primary"
      border="1px solid"
      borderColor={isDragging ? 'brand.500' : 'border.primary'}
      borderRadius="sm"
      cursor="pointer"
      _hover={{ bg: 'surface.secondary' }}
    >
      <Box
        {...attributes}
        {...listeners}
        cursor="grab"
        mr={2}
        color="text.tertiary"
        _hover={{ color: 'text.primary' }}
        onClick={(e) => e.stopPropagation()}
      >
        <MdDragIndicator size={16} />
      </Box>
      <Text fontSize="sm" flex={1} onClick={onClick}>
        {item.name}
      </Text>
    </Flex>
  );
}

interface BuildingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  building: Building | null;
  selectedFloor: Floor | null;
  onSelectFloor: (floor: Floor | null) => void;
  selectedRoom: Room | null;
  onSelectRoom: (room: Room | null) => void;
}

export function BuildingDrawer({
  isOpen,
  onClose,
  building,
  selectedFloor,
  onSelectFloor,
  selectedRoom,
  onSelectRoom,
}: BuildingDrawerProps) {
  const { t } = useTranslation();
  const [buildingName, setBuildingName] = useState('');
  const [buildingElevation, setBuildingElevation] = useState('');
  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState('');
  const [localOrder, setLocalOrder] = useState<FloorItem[]>([]);

  const { data, isLoading, error } = useFloors({
    building: building?.id,
    page_size: 50,
    ordering: 'ordering,name',
  });
  const createMutation = useCreateFloor();
  const updateBuildingMutation = useUpdateBuilding();
  const reorderMutation = useReorderFloors();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Sync API data into local order state
  useEffect(() => {
    if (data?.results) {
      setLocalOrder(
        data.results.map((f, idx) => ({
          id: String(f.id),
          numericId: f.id,
          name: f.name,
          ordering: f.ordering ?? idx,
        })),
      );
    }
  }, [data]);

  const filteredItems = useMemo(() => {
    if (!search) return localOrder;
    const q = search.toLowerCase();
    return localOrder.filter((item) => item.name.toLowerCase().includes(q));
  }, [localOrder, search]);

  useEffect(() => {
    if (building) {
      setBuildingName(building.name || building.building);
      setBuildingElevation(building.elevation != null ? String(building.elevation) : '');
      setDirty(false);
    }
  }, [building]);

  const handleClose = useCallback(() => {
    onSelectFloor(null);
    onSelectRoom(null);
    onClose();
  }, [onClose, onSelectFloor, onSelectRoom]);

  const handleFloorClick = useCallback(
    (floorId: string) => {
      const floor = data?.results.find((f) => String(f.id) === floorId);
      if (floor) {
        onSelectFloor(floor);
        onSelectRoom(null);
      }
    },
    [data, onSelectFloor, onSelectRoom],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = localOrder.findIndex((item) => item.id === active.id);
      const newIndex = localOrder.findIndex((item) => item.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(localOrder, oldIndex, newIndex);
      const updates = reordered.map((item, idx) => ({
        ...item,
        ordering: idx,
      }));
      setLocalOrder(updates);

      reorderMutation.mutate(
        updates.map(({ numericId, ordering }) => ({ id: numericId, ordering })),
        {
          onSuccess: () => {
            toaster.create({ title: 'Floor order saved', type: 'success', duration: 2000 });
          },
          onError: (err: unknown) => {
            toaster.create({
              title: 'Failed to save order',
              description: err instanceof Error ? err.message : 'Something went wrong',
              type: 'error',
              duration: 4000,
            });
          },
        },
      );
    },
    [localOrder, reorderMutation],
  );

  const handleAddFloor = useCallback(() => {
    if (!building) return;
    createMutation.mutate(
      { name: 'New Floor', building: String(building.id), school: building.school },
      {
        onSuccess: () => {
          toaster.create({ title: 'Floor created', type: 'success', duration: 3000 });
        },
        onError: (err: unknown) => {
          toaster.create({
            title: 'Error',
            description: err instanceof Error ? err.message : 'Something went wrong',
            type: 'error',
            duration: 4000,
          });
        },
      },
    );
  }, [building, createMutation]);

  const handleSaveBuilding = useCallback(() => {
    if (!building || !buildingName.trim()) return;
    const elevationNum = buildingElevation.trim() ? parseFloat(buildingElevation.trim()) : null;
    updateBuildingMutation.mutate(
      { id: building.id, data: { name: buildingName.trim(), building: buildingName.trim(), elevation: elevationNum } },
      {
        onSuccess: () => {
          toaster.create({ title: 'Building updated', type: 'success', duration: 3000 });
          setDirty(false);
        },
        onError: (err: unknown) => {
          toaster.create({
            title: 'Error',
            description: err instanceof Error ? err.message : 'Something went wrong',
            type: 'error',
            duration: 4000,
          });
        },
      },
    );
  }, [building, buildingName, buildingElevation, updateBuildingMutation]);

  const isDragDisabled = !!search;

  return (
    <>
      <DrawerComponent
        isOpen={isOpen}
        onClose={handleClose}
        title={building ? `Building — ${building.name || building.building}` : 'Building'}
        size="md"
        closeOnOutside={false}
        closeOnEsc={false}
        footer={
          <SimpleGrid columns={1} gap={4} width="100%">
            <ButtonComponent visual="secondary" onClick={handleClose} data-e2e="close-building-drawer-button">
              Close
            </ButtonComponent>
          </SimpleGrid>
        }
      >
        <Flex direction="column" gap={4}>
          <SectionCard title={t('sites.buildingDetails', 'Building Details')}>
            <Flex direction="column" gap={2}>
              <Field.Root>
                <Field.Label htmlFor="building-name">{t('common.name', 'Name')}</Field.Label>
                <InputField
                  id="building-name"
                  size="sm"
                  value={buildingName}
                  onChange={(e) => { setBuildingName(e.target.value); setDirty(true); }}
                  data-e2e="building-name-input"
                />
              </Field.Root>
              <Field.Root>
                <Field.Label htmlFor="building-elevation">Elevation</Field.Label>
                <InputField
                  id="building-elevation"
                  size="sm"
                  value={buildingElevation}
                  onChange={(e) => { setBuildingElevation(e.target.value); setDirty(true); }}
                  placeholder="0"
                  data-e2e="building-elevation-input"
                />
              </Field.Root>
            </Flex>
            {dirty && (
              <Flex gap={2} justifyContent="flex-end" mt={3}>
                <ButtonComponent
                  visual="secondary"
                  size="sm"
                  onClick={() => {
                    if (building) {
                      setBuildingName(building.name || building.building);
                      setBuildingElevation(building.elevation != null ? String(building.elevation) : '');
                      setDirty(false);
                    }
                  }}
                  data-e2e="cancel-building-details-button"
                >
                  {t('common.cancel', 'Cancel')}
                </ButtonComponent>
                <ButtonComponent
                  visual="primary"
                  size="sm"
                  onClick={handleSaveBuilding}
                  disabled={updateBuildingMutation.isPending}
                  data-e2e="save-building-details-button"
                >
                  {updateBuildingMutation.isPending ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                </ButtonComponent>
              </Flex>
            )}
          </SectionCard>

          <SectionCard
            title={t('sites.floors', 'Floors')}
            action={
              <ButtonComponent visual="primary" size="sm" onClick={handleAddFloor} data-e2e="add-floor-button">
                {t('sites.addFloor', 'Add Floor')}
              </ButtonComponent>
            }
          >
            <Field.Root>
              <Field.Label htmlFor="filter-floors" srOnly>
                Filter floors
              </Field.Label>
              <InputField
                id="filter-floors"
                size="sm"
                placeholder={t('sites.filterFloors', 'Filter floors...')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                mb={3}
                data-e2e="filter-floors-input"
              />
            </Field.Root>

            {isLoading ? (
              <Center py={8}>
                <Spinner size="lg" color="button.primary" />
              </Center>
            ) : error ? (
              <Center py={8}>
                <Text color="text.danger">{t('sites.failedLoadFloors', 'Failed to load floors')}</Text>
              </Center>
            ) : filteredItems.length === 0 ? (
              <Center py={8}>
                <Text color="text.secondary">{t('sites.noFloorsFound', 'No floors found')}</Text>
              </Center>
            ) : isDragDisabled ? (
              <Flex direction="column" gap={1}>
                {filteredItems.map((item) => (
                  <Flex
                    key={item.id}
                    alignItems="center"
                    px={3}
                    py={2}
                    bg="surface.primary"
                    border="1px solid"
                    borderColor="border.primary"
                    borderRadius="sm"
                    cursor="pointer"
                    _hover={{ bg: 'surface.secondary' }}
                    onClick={() => handleFloorClick(item.id)}
                  >
                    <Text fontSize="sm" flex={1}>{item.name}</Text>
                  </Flex>
                ))}
              </Flex>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredItems.map((i) => i.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Flex direction="column" gap={1}>
                    {filteredItems.map((item) => (
                      <SortableFloorItem
                        key={item.id}
                        item={item}
                        onClick={() => handleFloorClick(item.id)}
                      />
                    ))}
                  </Flex>
                </SortableContext>
              </DndContext>
            )}
          </SectionCard>
        </Flex>
      </DrawerComponent>

      <FloorDrawer
        isOpen={!!selectedFloor}
        onClose={() => onSelectFloor(null)}
        floor={selectedFloor}
        selectedRoom={selectedRoom}
        onSelectRoom={onSelectRoom}
      />
    </>
  );
}
